import { Theme } from '@monorepo/relayably/theme'
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import {
  NavigationContainer,
  DarkTheme as NavigationDarkTheme,
  DefaultTheme as NavigationDefaultTheme,
  NavigatorScreenParams,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, AppState, AppStateStatus, Linking, Platform, Text, View } from 'react-native'
import { logout } from '../auth/authSlice'
import { bootstrapSession, dismissError } from '../auth/authSlice'
import { AgentDetailScreen } from '../dashboard/components/AgentDetailScreen'
import { AgentsScreen } from '../dashboard/components/AgentsScreen'
import { AgentsTopTab } from '../dashboard/components/AgentsScreen'
import { CallDetailScreen } from '../dashboard/components/CallDetailScreen'
import { CallsScreen } from '../dashboard/components/CallsScreen'
import { DashboardShell } from '../dashboard/components/DashboardShell'
import { DirectScreen } from '../dashboard/components/DirectScreen'
import { OverviewScreen } from '../dashboard/components/OverviewScreen'
import { PhoneForwardingGuideModal } from '../dashboard/components/PhoneForwardingGuideModal'
import { SettingsActionState, SettingsScreen, SettingsSection } from '../dashboard/components/SettingsScreen'
import { phoneForwardingGuideStorage } from '../dashboard/phoneForwardingGuideStorage'
import {
  bootstrapDashboard,
  clearSelectedAgent,
  clearSelectedCall,
  deleteInstruction,
  deleteKnowledgeItem,
  deployAgentDetail,
  fetchAgentDetail,
  fetchCallDetail,
  saveAgentDetail,
  saveInstruction,
  saveKnowledgeItem,
  saveChat,
  uploadKnowledgeFile,
} from '../dashboard/dashboardSlice'
import { DashboardTab } from '../dashboard/types'
import { AuthFlow } from '../flows/auth/AuthFlow'
import { LinkedInCallbackExperience } from '../LinkedInCallbackExperience'
import { useAppDispatch, useAppSelector } from '../store'
import { WorkspaceOnboardingExperience } from '../workspace/WorkspaceOnboardingExperience'
import { relayablyPhoneNumberApi } from '../workspace/relayablyPhoneNumberApi'
import { RelayablyPaymentProduct } from '../workspace/types'
import { relayablyWorkspaceApi } from '../workspace/relayablyWorkspaceApi'
import { applyWorkspaceDraft, bootstrapWorkspace, replaceWorkspace, saveWorkspace } from '../workspace/workspaceSlice'

type DashboardTabParamList = {
  Home: undefined
  Talk: { section?: 'inbound' | 'outbound' } | undefined
  Chat: undefined
  Agents: { section?: AgentsTopTab } | undefined
  Settings: { section?: SettingsSection } | undefined
}

type DashboardStackParamList = {
  Tabs: NavigatorScreenParams<DashboardTabParamList> | undefined
  CallDetail: { callId: string }
  AgentDetail: { agentId: string; section?: 'details' | 'workflow' }
}

type RootStackParamList = {
  Auth: undefined
  LinkedInCallback: undefined
  Onboarding: undefined
  Dashboard: NavigatorScreenParams<DashboardStackParamList> | undefined
}

const routeCopy: Record<
  DashboardTab,
  {
    title: string
    subtitle: string
  }
> = {
  home: {
    title: 'Your calls at a glance',
    subtitle: 'See what Relayably handled this week, where calls came from, and which agents carried the load.',
  },
  talk: {
    title: 'Talk history',
    subtitle: 'Read what happened, understand the outcome fast, and jump into the full transcript when needed.',
  },
  chat: {
    title: 'Chat',
    subtitle: 'Tell the system what to do next in plain language, by voice or text.',
  },
  agents: {
    title: 'Agent manager',
    subtitle: 'Open each agent, update the prompt, review the workflow, and keep your call handling aligned.',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Review your account, workspace, and current number setup.',
  },
}

const DashboardTabs = createBottomTabNavigator<DashboardTabParamList>()
const DashboardStack = createNativeStackNavigator<DashboardStackParamList>()
const RootStack = createNativeStackNavigator<RootStackParamList>()
const billingPortalReturnFlag = 'relayably-billing-portal-pending'

const DashboardChromeContext = createContext<{
  collapsed: boolean
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>
} | null>(null)

const useDashboardChrome = () => {
  const context = useContext(DashboardChromeContext)
  if (!context) {
    throw new Error('Relayably dashboard chrome is unavailable.')
  }

  return context
}

const DashboardScaffold = ({
  theme,
  activeTab,
  title,
  subtitle,
  children,
  onBack,
  sidebarSections,
}: {
  theme: Theme
  activeTab: DashboardTab
  title: string
  subtitle: string
  children: React.ReactNode
  onBack?: () => void
  sidebarSections?: Array<{
    key: string
    label: string
    active: boolean
    onPress: () => void
  }>
}) => {
  const { collapsed, setCollapsed } = useDashboardChrome()
  const navigation = useNavigation<any>()
  const access = useAppSelector(state => state.workspace.access)
  const notifications = useAppSelector(state => state.workspace.notifications)
  const lockedToSettings = access?.state === 'disabled'

  useEffect(() => {
    if (lockedToSettings && activeTab !== 'settings') {
      navigation.navigate('Tabs', { screen: 'Settings', params: { section: 'account' } })
    }
  }, [activeTab, lockedToSettings, navigation])

  const navigateTab = (tab: DashboardTab) => {
    if (lockedToSettings && tab !== 'settings') {
      return
    }

    if (tab === 'home') {
      navigation.navigate('Tabs', { screen: 'Home' })
      return
    }

    if (tab === 'talk') {
      navigation.navigate('Tabs', { screen: 'Talk' })
      return
    }

    if (tab === 'chat') {
      navigation.navigate('Tabs', { screen: 'Chat' })
      return
    }

    if (tab === 'agents') {
      navigation.navigate('Tabs', { screen: 'Agents' })
      return
    }

    navigation.navigate('Tabs', { screen: 'Settings' })
  }

  return (
    <DashboardShell
      theme={theme}
      route={
        onBack
          ? activeTab === 'talk'
            ? { kind: 'call', tab: 'talk', callId: '' }
            : { kind: 'agent', tab: 'agents', agentId: '' }
          : { kind: 'tab', tab: activeTab }
      }
      title={title}
      subtitle={subtitle}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed(previous => !previous)}
      onBack={onBack}
      onSelectTab={navigateTab}
      disabledTabs={lockedToSettings ? ['home', 'talk', 'chat', 'agents'] : []}
      sidebarSections={sidebarSections}
    >
      <View style={{ gap: 12, flex: 1 }}>
        {notifications.slice(0, 3).map(notification => (
          <View
            key={notification.id}
            style={{
              borderRadius: 18,
              paddingHorizontal: 16,
              paddingVertical: 14,
              backgroundColor:
                notification.level === 'critical'
                  ? 'rgba(183, 68, 68, 0.18)'
                  : notification.level === 'warning'
                  ? 'rgba(201, 148, 38, 0.18)'
                  : 'rgba(124, 136, 244, 0.14)',
              borderWidth: 1,
              borderColor:
                notification.level === 'critical'
                  ? 'rgba(255, 182, 182, 0.28)'
                  : notification.level === 'warning'
                  ? 'rgba(245, 201, 95, 0.24)'
                  : 'rgba(124, 136, 244, 0.22)',
            }}
          >
            <Text style={{ color: theme.colors.text, fontSize: 15, fontWeight: '700', marginBottom: 4 }}>{notification.title}</Text>
            <Text style={{ color: theme.colors.inactive, fontSize: 13, lineHeight: 20 }}>{notification.message}</Text>
          </View>
        ))}
        {children}
      </View>
    </DashboardShell>
  )
}

const HomeTabScreen = ({ theme }: { theme: Theme }) => {
  const navigation = useNavigation<any>()
  const dashboard = useAppSelector(state => state.dashboard)

  return (
    <DashboardScaffold theme={theme} activeTab='home' {...routeCopy.home}>
      {dashboard.overview ? (
        <OverviewScreen
          theme={theme}
          overview={dashboard.overview}
          onOpenCall={call => navigation.navigate('CallDetail', { callId: call.id })}
        />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      )}
    </DashboardScaffold>
  )
}

const TalkTabScreen = ({ theme }: { theme: Theme }) => {
  const navigation = useNavigation<any>()
  const route = useRoute<any>()
  const dashboard = useAppSelector(state => state.dashboard)
  const currentSection = (route.params?.section ?? 'inbound') as 'inbound' | 'outbound'

  return (
    <DashboardScaffold
      theme={theme}
      activeTab='talk'
      sidebarSections={[
        {
          key: 'inbound',
          label: 'Inbound',
          active: currentSection === 'inbound',
          onPress: () => navigation.navigate('Talk', { section: 'inbound' }),
        },
        {
          key: 'outbound',
          label: 'Outbound',
          active: currentSection === 'outbound',
          onPress: () => navigation.navigate('Talk', { section: 'outbound' }),
        },
      ]}
      {...routeCopy.talk}
    >
      <CallsScreen
        theme={theme}
        calls={dashboard.calls}
        direction={currentSection}
        onDirectionChange={section => navigation.navigate('Talk', { section })}
        onOpenCall={call => navigation.navigate('CallDetail', { callId: call.id })}
      />
    </DashboardScaffold>
  )
}

const ChatTabScreen = ({ theme }: { theme: Theme }) => {
  const dispatch = useAppDispatch()
  const dashboard = useAppSelector(state => state.dashboard)

  return (
    <DashboardScaffold theme={theme} activeTab='chat' {...routeCopy.chat}>
      <DirectScreen
        theme={theme}
        status={dashboard.strategyStatus}
        message={dashboard.strategyStatus === 'error' ? dashboard.errorMessage : dashboard.chatMessage}
        onSubmit={payload => void dispatch(saveChat(payload))}
      />
    </DashboardScaffold>
  )
}

const AgentsTabScreen = ({ theme }: { theme: Theme }) => {
  const dispatch = useAppDispatch()
  const navigation = useNavigation<any>()
  const route = useRoute<any>()
  const dashboard = useAppSelector(state => state.dashboard)
  const workspace = useAppSelector(state => state.workspace.currentWorkspace)
  const sessionToken = useAppSelector(state => state.auth.sessionToken)
  const currentSection = (route.params?.section ?? 'agents') as AgentsTopTab

  return (
    <DashboardScaffold
      theme={theme}
      activeTab='agents'
      sidebarSections={[
        {
          key: 'agents',
          label: 'Agents',
          active: currentSection === 'agents',
          onPress: () => navigation.navigate('Agents', { section: 'agents' }),
        },
        {
          key: 'voice',
          label: 'Voice',
          active: currentSection === 'voice',
          onPress: () => navigation.navigate('Agents', { section: 'voice' }),
        },
        {
          key: 'knowledge',
          label: 'Knowledge',
          active: currentSection === 'knowledge',
          onPress: () => navigation.navigate('Agents', { section: 'knowledge' }),
        },
        {
          key: 'instructions',
          label: 'Instructions',
          active: currentSection === 'instructions',
          onPress: () => navigation.navigate('Agents', { section: 'instructions' }),
        },
      ]}
      {...routeCopy.agents}
    >
      <AgentsScreen
        theme={theme}
        agents={dashboard.agents}
        workspace={workspace}
        sessionToken={sessionToken}
        activeTab={currentSection}
        onTabChange={section => navigation.navigate('Agents', { section })}
        onOpenAgent={agent => navigation.navigate('AgentDetail', { agentId: agent.id })}
        onSaveCompanyProfile={payload => {
          dispatch(
            applyWorkspaceDraft({
              workspace: {
                businessName: payload.businessName,
                website: payload.website,
                businessDescription: payload.businessDescription,
              },
            })
          )
          void dispatch(saveWorkspace())
        }}
        onSaveKnowledgeItem={payload => void dispatch(saveKnowledgeItem(payload))}
        onDeleteKnowledgeItem={knowledgeItemId => void dispatch(deleteKnowledgeItem(knowledgeItemId))}
        onUploadKnowledgeFile={payload => void dispatch(uploadKnowledgeFile(payload))}
        onSaveInstruction={payload => void dispatch(saveInstruction(payload))}
        onDeleteInstruction={instructionId => void dispatch(deleteInstruction(instructionId))}
        onSelectVoice={voice => {
          dispatch(
            applyWorkspaceDraft({
              workspace: {
                voiceSelection: voice,
              },
            })
          )
          void dispatch(saveWorkspace())
        }}
        onSelectAgentVoice={(agentId, voice) => {
          const nextAgentConfigs = (workspace?.workspace.agentConfigs ?? []).map(agent =>
            agent.id === agentId
              ? {
                  ...agent,
                  voiceSelection: voice,
                }
              : agent
          )

          dispatch(
            applyWorkspaceDraft({
              workspace: {
                agentConfigs: nextAgentConfigs,
              },
            })
          )
          void dispatch(saveWorkspace())
        }}
      />
    </DashboardScaffold>
  )
}

const SettingsTabScreen = ({ theme, onOpenPhoneForwardingGuide }: { theme: Theme; onOpenPhoneForwardingGuide: () => void }) => {
  const dispatch = useAppDispatch()
  const navigation = useNavigation<any>()
  const route = useRoute<any>()
  const auth = useAppSelector(state => state.auth)
  const workspace = useAppSelector(state => state.workspace.currentWorkspace)
  const workspaceStatus = useAppSelector(state => state.workspace.status)
  const billing = useAppSelector(state => state.workspace.billing)
  const [billingProducts, setBillingProducts] = useState<RelayablyPaymentProduct[]>([])
  const [billingLoading, setBillingLoading] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [actionState, setActionState] = useState<SettingsActionState>({ type: 'idle' })
  const [awaitingExternalBillingReturn, setAwaitingExternalBillingReturn] = useState(false)
  const currentSection = (route.params?.section ?? 'account') as SettingsSection
  const showCompanySection = workspace?.workspace.usageType ? workspace.workspace.usageType !== 'personal' : false
  const normalizedSection = showCompanySection || currentSection !== 'company' ? currentSection : 'account'

  const refreshWorkspaceState = React.useCallback(() => {
    if (!auth.sessionToken) {
      return
    }

    void Promise.allSettled([dispatch(bootstrapWorkspace()).unwrap(), dispatch(bootstrapSession()).unwrap()])
  }, [auth.sessionToken, dispatch])

  const markBillingPortalPending = React.useCallback(() => {
    if (Platform.OS === 'web' && typeof globalThis.sessionStorage !== 'undefined') {
      globalThis.sessionStorage.setItem(billingPortalReturnFlag, '1')
      return
    }

    setAwaitingExternalBillingReturn(true)
  }, [])

  const consumePendingBillingPortalRefresh = React.useCallback(() => {
    if (Platform.OS === 'web' && typeof globalThis.sessionStorage !== 'undefined') {
      if (globalThis.sessionStorage.getItem(billingPortalReturnFlag) !== '1') {
        return
      }

      globalThis.sessionStorage.removeItem(billingPortalReturnFlag)
      refreshWorkspaceState()
      return
    }

    if (awaitingExternalBillingReturn) {
      setAwaitingExternalBillingReturn(false)
      refreshWorkspaceState()
    }
  }, [awaitingExternalBillingReturn, refreshWorkspaceState])

  const openExternalUrl = async (url: string) => {
    markBillingPortalPending()

    if (Platform.OS === 'web' && typeof globalThis.open === 'function') {
      globalThis.open(url, '_self')
      return
    }

    await Linking.openURL(url)
  }

  useFocusEffect(
    React.useCallback(() => {
      refreshWorkspaceState()
    }, [refreshWorkspaceState])
  )

  useEffect(() => {
    if (!auth.sessionToken) {
      return
    }

    const handleVisibilityOrFocus = () => {
      if (Platform.OS === 'web' && typeof globalThis.document !== 'undefined' && globalThis.document.visibilityState !== 'visible') {
        return
      }

      consumePendingBillingPortalRefresh()
    }

    const handleAppStateChange = (status: AppStateStatus) => {
      if (status === 'active') {
        consumePendingBillingPortalRefresh()
      }
    }

    if (Platform.OS === 'web' && typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener('focus', handleVisibilityOrFocus)
    }

    if (Platform.OS === 'web' && typeof globalThis.document !== 'undefined') {
      globalThis.document.addEventListener('visibilitychange', handleVisibilityOrFocus)
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)
    handleVisibilityOrFocus()

    return () => {
      if (Platform.OS === 'web' && typeof globalThis.removeEventListener === 'function') {
        globalThis.removeEventListener('focus', handleVisibilityOrFocus)
      }

      if (Platform.OS === 'web' && typeof globalThis.document !== 'undefined') {
        globalThis.document.removeEventListener('visibilitychange', handleVisibilityOrFocus)
      }

      subscription.remove()
    }
  }, [auth.sessionToken, consumePendingBillingPortalRefresh])

  useEffect(() => {
    if (!auth.sessionToken || !workspace?.workspace.phoneNumber || workspace.workspace.phoneNumber.status !== 'pending_review') {
      return
    }

    let cancelled = false

    void relayablyPhoneNumberApi.getCurrentNumber(auth.sessionToken).then(response => {
      if (cancelled || !response.number || response.number.status === workspace.workspace.phoneNumber?.status) {
        return
      }

      dispatch(
        replaceWorkspace({
          ...workspace,
          workspace: {
            ...workspace.workspace,
            phoneNumber: response.number,
          },
        })
      )
    })

    return () => {
      cancelled = true
    }
  }, [auth.sessionToken, dispatch, workspace])

  useEffect(() => {
    if (!auth.sessionToken) {
      return
    }

    let cancelled = false
    setBillingLoading(true)
    setBillingError(null)

    void relayablyWorkspaceApi
      .listPaymentProducts(auth.sessionToken)
      .then(response => {
        if (cancelled) {
          return
        }

        setBillingProducts(response.products)
      })
      .catch(error => {
        if (cancelled) {
          return
        }

        setBillingError(error instanceof Error ? error.message : 'Relayably could not load billing plans right now.')
      })
      .finally(() => {
        if (!cancelled) {
          setBillingLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [auth.sessionToken])

  if (!auth.user) {
    return null
  }

  return (
    <DashboardScaffold
      theme={theme}
      activeTab='settings'
      sidebarSections={[
        {
          key: 'account',
          label: 'Account',
          active: normalizedSection === 'account',
          onPress: () => navigation.navigate('Settings', { section: 'account' }),
        },
        ...(showCompanySection
          ? [
              {
                key: 'company',
                label: 'Company Profile',
                active: normalizedSection === 'company',
                onPress: () => navigation.navigate('Settings', { section: 'company' }),
              },
            ]
          : []),
        {
          key: 'help',
          label: 'Help & Service',
          active: normalizedSection === 'help',
          onPress: () => navigation.navigate('Settings', { section: 'help' }),
        },
      ]}
      {...routeCopy.settings}
    >
      <SettingsScreen
        activeSection={normalizedSection}
        billingError={billingError}
        billingLoading={billingLoading}
        billingProducts={billingProducts}
        billing={billing}
        workspaceLoading={workspaceStatus === 'loading'}
        actionState={actionState}
        theme={theme}
        user={auth.user}
        workspace={workspace}
        onSectionChange={section => navigation.navigate('Settings', { section })}
        onOpenBillingPortal={() => {
          if (!auth.sessionToken || actionState.type !== 'idle') {
            return
          }

          setBillingError(null)
          setActionState({ type: 'portal' })
          void relayablyWorkspaceApi
            .createPortalSession(auth.sessionToken)
            .then(response => openExternalUrl(response.portalUrl))
            .catch(error => setBillingError(error instanceof Error ? error.message : 'Relayably could not open billing right now.'))
            .finally(() => setActionState({ type: 'idle' }))
        }}
        onCancelSubscription={() => {
          if (!auth.sessionToken || actionState.type !== 'idle') {
            return
          }

          setBillingError(null)
          setActionState({ type: 'cancel' })
          void relayablyWorkspaceApi
            .cancelSubscription(auth.sessionToken)
            .then(() => Promise.all([dispatch(bootstrapWorkspace()).unwrap(), dispatch(bootstrapSession()).unwrap()]))
            .catch(error => setBillingError(error instanceof Error ? error.message : 'Relayably could not cancel your subscription right now.'))
            .finally(() => setActionState({ type: 'idle' }))
        }}
        onSaveCompanyProfile={payload => {
          dispatch(
            applyWorkspaceDraft({
              workspace: {
                businessName: payload.businessName,
                website: payload.website,
                businessDescription: payload.businessDescription,
              },
            })
          )
          void dispatch(saveWorkspace())
        }}
        onSaveAccount={payload => {
          if (!auth.sessionToken || !workspace) {
            return
          }

          setBillingError(null)
          dispatch(
            applyWorkspaceDraft({
              profile: {
                fullName: payload.fullName,
                country: payload.country,
              },
              workspace: {
                workspaceName: payload.workspaceName,
              },
            })
          )

          void relayablyWorkspaceApi
            .updateUserProfile(auth.sessionToken, {
              fullName: payload.fullName,
              phoneNumber: payload.phoneNumber,
              country: payload.country,
              timezone: workspace.profile.timezone,
            })
            .then(() => dispatch(saveWorkspace()).unwrap())
            .then(() => Promise.all([dispatch(bootstrapSession()).unwrap(), dispatch(bootstrapWorkspace()).unwrap()]))
            .catch(error => setBillingError(error instanceof Error ? error.message : 'Relayably could not save your account settings right now.'))
        }}
        onStartCheckout={planId => {
          if (!auth.sessionToken || actionState.type !== 'idle') {
            return
          }

          setBillingError(null)
          setActionState({ type: 'checkout', planId })
          dispatch(
            applyWorkspaceDraft({
              workspace: {
                usageType: planId,
              },
            })
          )
          void dispatch(saveWorkspace())
            .unwrap()
            .then(() => relayablyWorkspaceApi.createCheckoutSession(auth.sessionToken!, planId))
            .then(response => {
              if (!response.checkoutUrl) {
                throw new Error('Relayably could not start checkout for this plan.')
              }

              return openExternalUrl(response.checkoutUrl)
            })
            .catch(error => setBillingError(error instanceof Error ? error.message : 'Relayably could not start checkout right now.'))
            .finally(() => setActionState({ type: 'idle' }))
        }}
        onLogout={() => void dispatch(logout(auth.sessionToken))}
        onOpenPhoneForwardingGuide={onOpenPhoneForwardingGuide}
      />
    </DashboardScaffold>
  )
}

const CallDetailRoute = ({ theme, callId }: { theme: Theme; callId: string }) => {
  const dispatch = useAppDispatch()
  const navigation = useNavigation<any>()
  const dashboard = useAppSelector(state => state.dashboard)

  useEffect(() => {
    void dispatch(fetchCallDetail(callId))
  }, [callId, dispatch])

  return (
    <DashboardScaffold
      theme={theme}
      activeTab='talk'
      title='Call detail'
      subtitle='Read the transcript, understand the intent, and keep track of what happened.'
      onBack={() => {
        dispatch(clearSelectedCall())
        navigation.goBack()
      }}
    >
      {dashboard.selectedCall ? (
        <CallDetailScreen theme={theme} call={dashboard.selectedCall} />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      )}
    </DashboardScaffold>
  )
}

const AgentDetailRoute = ({ theme, agentId, section = 'details' }: { theme: Theme; agentId: string; section?: 'details' | 'workflow' }) => {
  const dispatch = useAppDispatch()
  const navigation = useNavigation<any>()
  const dashboard = useAppSelector(state => state.dashboard)

  useEffect(() => {
    void dispatch(fetchAgentDetail(agentId))
  }, [agentId, dispatch])

  return (
    <DashboardScaffold
      theme={theme}
      activeTab='agents'
      title='Agent detail'
      subtitle='Update the language this agent uses and review where it hands calls next.'
      sidebarSections={[
        {
          key: 'details',
          label: 'Details',
          active: section === 'details',
          onPress: () => navigation.replace('AgentDetail', { agentId, section: 'details' }),
        },
        {
          key: 'workflow',
          label: 'Workflow',
          active: section === 'workflow',
          onPress: () => navigation.replace('AgentDetail', { agentId, section: 'workflow' }),
        },
      ]}
      onBack={() => {
        dispatch(clearSelectedAgent())
        navigation.goBack()
      }}
    >
      {dashboard.selectedAgent ? (
        <AgentDetailScreen
          theme={theme}
          agent={dashboard.selectedAgent}
          activeTab={section}
          onTabChange={nextSection => navigation.replace('AgentDetail', { agentId, section: nextSection })}
          onSave={payload => void dispatch(saveAgentDetail({ agentId: dashboard.selectedAgent!.agent.id, payload }))}
          onDeploy={payload => void dispatch(deployAgentDetail({ agentId: dashboard.selectedAgent!.agent.id, payload }))}
        />
      ) : (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      )}
    </DashboardScaffold>
  )
}

const DashboardTabsNavigator = ({
  theme,
  onOpenPhoneForwardingGuide,
}: {
  theme: Theme
  onOpenPhoneForwardingGuide: () => void
}) => (
  <DashboardTabs.Navigator screenOptions={{ headerShown: false, tabBarStyle: { display: 'none' } }}>
    <DashboardTabs.Screen name='Home'>{() => <HomeTabScreen theme={theme} />}</DashboardTabs.Screen>
    <DashboardTabs.Screen name='Talk'>{() => <TalkTabScreen theme={theme} />}</DashboardTabs.Screen>
        <DashboardTabs.Screen name='Chat'>{() => <ChatTabScreen theme={theme} />}</DashboardTabs.Screen>
    <DashboardTabs.Screen name='Agents'>{() => <AgentsTabScreen theme={theme} />}</DashboardTabs.Screen>
    <DashboardTabs.Screen name='Settings'>{() => <SettingsTabScreen theme={theme} onOpenPhoneForwardingGuide={onOpenPhoneForwardingGuide} />}</DashboardTabs.Screen>
  </DashboardTabs.Navigator>
)

const DashboardNavigator = ({ theme }: { theme: Theme }) => {
  const dispatch = useAppDispatch()
  const auth = useAppSelector(state => state.auth)
  const workspace = useAppSelector(state => state.workspace.currentWorkspace)
  const [collapsed, setCollapsed] = useState(false)
  const [showPhoneForwardingGuide, setShowPhoneForwardingGuide] = useState(false)

  useEffect(() => {
    if (!auth.sessionToken) {
      return
    }

    if (!workspace) {
      void dispatch(bootstrapWorkspace())
    }

    void dispatch(bootstrapDashboard())
  }, [auth.sessionToken, dispatch, workspace])

  useEffect(() => {
    if (!workspace?.id || !workspace.workspace.phoneNumber || workspace.workspace.phoneNumber.status !== 'active') {
      return
    }

    let cancelled = false

    void phoneForwardingGuideStorage.getPendingWorkspaceId().then(pendingWorkspaceId => {
      if (cancelled) {
        return
      }

      if (pendingWorkspaceId === workspace.id) {
        setShowPhoneForwardingGuide(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [workspace])

  const chromeValue = useMemo(() => ({ collapsed, setCollapsed }), [collapsed])

  return (
    <DashboardChromeContext.Provider value={chromeValue}>
      <>
        <DashboardStack.Navigator screenOptions={{ headerShown: false }}>
          <DashboardStack.Screen name='Tabs'>
            {() => <DashboardTabsNavigator theme={theme} onOpenPhoneForwardingGuide={() => setShowPhoneForwardingGuide(true)} />}
          </DashboardStack.Screen>
          <DashboardStack.Screen name='CallDetail'>
            {({ route }) => <CallDetailRoute theme={theme} callId={route.params.callId} />}
          </DashboardStack.Screen>
          <DashboardStack.Screen name='AgentDetail'>
            {({ route }) => <AgentDetailRoute theme={theme} agentId={route.params.agentId} section={route.params.section} />}
          </DashboardStack.Screen>
        </DashboardStack.Navigator>
        <PhoneForwardingGuideModal
          theme={theme}
          visible={showPhoneForwardingGuide}
          phoneNumber={workspace?.workspace.phoneNumber?.phoneNumber}
          onClose={() => {
            setShowPhoneForwardingGuide(false)
            void phoneForwardingGuideStorage.clearPending()
          }}
        />
      </>
    </DashboardChromeContext.Provider>
  )
}

const LinkedInCallbackRoute = ({ theme }: { theme: Theme }) => {
  const navigation = useNavigation<any>()
  const auth = useAppSelector(state => state.auth)

  useEffect(() => {
    if (!auth.user) {
      return
    }

    navigation.replace(auth.user.onboardingComplete ? 'Dashboard' : 'Onboarding')
  }, [auth.user, navigation])

  return <LinkedInCallbackExperience />
}

const linking: any = {
  prefixes: ['/', 'http://localhost:8081', 'http://localhost:19006'],
  config: {
    screens: {
      Auth: '',
      LinkedInCallback: 'auth/linkedin/callback',
      Onboarding: 'setup',
      Dashboard: {
        screens: {
          Tabs: {
            screens: {
              Home: 'home',
              Talk: 'talk/:section?',
              Chat: 'chat',
              Agents: 'agents/:section?',
              Settings: 'settings',
            },
          },
          CallDetail: 'talk/call/:callId',
          AgentDetail: 'agents/:agentId/:section?',
        },
      },
    },
  },
}

const createNavigationTheme = (theme: Theme) => ({
  ...(theme.dark ? NavigationDarkTheme : NavigationDefaultTheme),
  colors: {
    ...(theme.dark ? NavigationDarkTheme.colors : NavigationDefaultTheme.colors),
    background: theme.colors.background,
    border: theme.colors.border,
    card: theme.colors.card,
    notification: theme.colors.notification,
    primary: theme.colors.primary,
    text: theme.colors.text,
  },
})

export const AppNavigator = ({ theme }: { theme: Theme }) => {
  const dispatch = useAppDispatch()
  const auth = useAppSelector(state => state.auth)
  const navigationTheme = useMemo(() => createNavigationTheme(theme), [theme])

  useEffect(() => {
    if (!auth.hydrated) {
      void dispatch(bootstrapSession())
    }
  }, [auth.hydrated, dispatch])

  if (!auth.hydrated) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.background }}>
        <ActivityIndicator color={theme.colors.primary} />
      </View>
    )
  }

  return (
    <NavigationContainer
      linking={linking}
      theme={navigationTheme}
      onStateChange={() => {
        if (auth.errorMessage) {
          dispatch(dismissError())
        }
      }}
    >
      <RootStack.Navigator screenOptions={{ headerShown: false }}>
        {auth.user ? (
          auth.user.onboardingComplete ? (
            <>
              <RootStack.Screen name='Dashboard'>{() => <DashboardNavigator theme={theme} />}</RootStack.Screen>
              <RootStack.Screen name='LinkedInCallback'>{() => <LinkedInCallbackRoute theme={theme} />}</RootStack.Screen>
            </>
          ) : (
            <>
              <RootStack.Screen name='Onboarding'>{() => <WorkspaceOnboardingExperience theme={theme} />}</RootStack.Screen>
              <RootStack.Screen name='LinkedInCallback'>{() => <LinkedInCallbackRoute theme={theme} />}</RootStack.Screen>
            </>
          )
        ) : (
          <>
            <RootStack.Screen name='Auth'>{() => <AuthFlow theme={theme} />}</RootStack.Screen>
            <RootStack.Screen name='LinkedInCallback'>{() => <LinkedInCallbackRoute theme={theme} />}</RootStack.Screen>
          </>
        )}
      </RootStack.Navigator>
    </NavigationContainer>
  )
}
